import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'src/app/servicios/message/message.service';
import { SharedService } from 'src/app/utils/shared/shared.service';
import { DatabaseService } from 'src/app/servicios/database/database.service';
import { MultipleDonwloadService } from '../multiple-download/multiple-donwload.service';

type DonwnloadPendingProcessed = any;
type DownloadsSummary = {
    message: string;
    totalFiles: number;
    errors: number;
    success: number;
    totalSize: string;
};

@Injectable({ providedIn: 'root' })
export class DownloadManagerService {
    private messageService = inject(MessageService);
    private _sharedService = inject(SharedService);
    private _multipleDonwloadService = inject(MultipleDonwloadService);
    private databaseService = inject(DatabaseService);
    private translateService = inject(TranslateService);

    // estado público para UI flotante
    public progress$ = new BehaviorSubject<number>(0);
    public active$ = new BehaviorSubject<boolean>(false);
    public batchInfo$ = new BehaviorSubject<{ count: number; sizeFormatted: string; rawSize: number } | null>(null);

    //estado por-archivo
    public currentIndex$ = new BehaviorSubject<number | null>(null);
    public currentFileProgress$ = new BehaviorSubject<number>(0);
    public currentFileName$ = new BehaviorSubject<string | null>(null);

    private controller: AbortController | null = null;
    private isCancelDownloads = false;
    private donwloadsFailed: DonwnloadPendingProcessed[] = [];

    /**
     * Inicia una tanda de descargas múltiples
     * @param files - lista de archivos a descargar
     * @returns
     */
    async startBatch(files: DonwnloadPendingProcessed[]) {
        if (!files || files.length === 0) return;

        const fileSizes = files.map((f) => {
            try { return +(f.playbackUri?.split('size=')[1] ?? 0) || 0; } catch { return 0; }
        });
        const totalBytes = fileSizes.reduce((s, x) => s + x, 0);
        const totalSizeFormatted = this._sharedService.formatFileSize(totalBytes);

        this.batchInfo$.next({ count: files.length, sizeFormatted: totalSizeFormatted, rawSize: totalBytes });
        this.active$.next(true);
        this.progress$.next(0);
        this.donwloadsFailed = [];
        this.isCancelDownloads = false;

        // pedir carpeta
        if (!('showDirectoryPicker' in window)) {
            await this.messageService.SwalMessage?.('error', this.translations?.funcion_no_disponible_en_navegador ?? 'Función no disponible en este navegador');
            this.resetState();
            return;
        }

        let directoryHandle: FileSystemDirectoryHandle;
        try {
            directoryHandle = await (window as any).showDirectoryPicker();
            const isPermissionGranted = await this._multipleDonwloadService.verifyPermissionFileSystemDirectoryHandle(directoryHandle);
            if (!isPermissionGranted) {
                await this.messageService.SwalMessage?.(
                    'error',
                    this.translations?.debes_conceder_permiso_para_guardar_los_archivos
                        ?? this.translateService.instant('debes_conceder_permiso_para_guardar_los_archivos'),
                );
                this.resetState();
                return;
            }
        } catch {
            this.resetState();
            return;
        }

        try {
            const downloadsResp = await Promise.all(files.map((file) => lastValueFrom(this.databaseService.traerUrlVideo(file))));
            let totalSizeFilesDownloaded = 0;
            const downloadsSummary: DownloadsSummary = { message: '', totalFiles: files.length, errors: 0, success: 0, totalSize: '' };

            this.controller = new AbortController();

            // SECUENCIAL: mantiene UX original. Para paralelizar se haría distinto.
            for (let index = 0; index < downloadsResp.length; index++) {
                if (this.isCancelDownloads) break;

                const { Descargas } = downloadsResp[index] as any;
                const urlFile = Descargas?.url;
                const currentFile = files[index];

                // actualizar estado por-archivo ANTES de iniciar
                this.currentIndex$.next(index + 1);
                this.currentFileName$.next(currentFile?.archivo || currentFile?.nombre_camara || `Archivo ${index + 1}`);
                this.currentFileProgress$.next(0);

                if (!urlFile) {
                    downloadsSummary.errors++;
                    this.donwloadsFailed.push(currentFile);
                    continue;
                }

                try {
                    const responseRequest = await fetch(urlFile, { signal: this.controller!.signal });
                    if (!responseRequest.ok) {
                        downloadsSummary.errors++;
                        this.donwloadsFailed.push(currentFile);
                        continue;
                    }

                    const contentLengthHeader = responseRequest.headers.get('Content-Length');
                    const contentLength = contentLengthHeader ? +contentLengthHeader : +(currentFile.playbackUri?.split('size=')[1] ?? 0) || 0;

                    const reader = responseRequest.body?.getReader();
                    if (!reader) {
                        downloadsSummary.errors++;
                        this.donwloadsFailed.push(currentFile);
                        continue;
                    }

                    let receivedLength = 0;
                    const chunksFile: Uint8Array[] = [];
                    const startTimeDownload = Date.now();

                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        if (value) {
                            const chunk = value instanceof Uint8Array ? value : new Uint8Array(value as ArrayBuffer);
                            chunksFile.push(chunk);
                            receivedLength += chunk.byteLength;

                            // progreso por archivo
                            const progressDownload = contentLength > 0 ? Math.floor((receivedLength / contentLength) * 100) : 0;
                            this.currentFileProgress$.next(progressDownload);

                            // actualizar progreso global aproximado
                            if (contentLength > 0) {
                                const prevDownloaded = totalSizeFilesDownloaded;
                                const percentGlobal = totalBytes > 0 ? Math.round(((prevDownloaded + receivedLength) / totalBytes) * 100) : 0;
                                this.progress$.next(Math.min(100, percentGlobal));
                            }

                            // actualizar tiempo restante / barra DOM legacy
                            const elapsedTime = (Date.now() - startTimeDownload) / 1000;
                            const speed = elapsedTime > 0 ? receivedLength / elapsedTime : 0;
                            const bytesRemaining = contentLength > 0 ? Math.max(0, contentLength - receivedLength) : 0;
                            const secondsRemaining = speed > 0 ? bytesRemaining / speed : 0;
                            const formatted = this._multipleDonwloadService.formattedTimeRestant(secondsRemaining);
                            const formattedTime = `${formatted.timeRestant} ${this.translations?.[formatted.unit] ?? formatted.unit}`;
                            const timeEl = document.getElementById('time-download-remain') as HTMLElement | null;
                            if (timeEl && progressDownload < 100) timeEl.innerText = `${this.translations?.tiempo ?? 'Tiempo'} ${this.translations?.restante ?? 'restante'}: ${formattedTime}`;
                        }
                        if (this.isCancelDownloads) break;
                    }

                    if (this.isCancelDownloads) {
                        try { this.controller?.abort(); } catch { }
                        downloadsSummary.errors++;
                        this.donwloadsFailed.push(currentFile);
                        break;
                    }

                    // concatenar chunks y guardar
                    const totalLength = chunksFile.reduce((acc, c) => acc + c.byteLength, 0);
                    const uint8 = new Uint8Array(totalLength);
                    let offset = 0;
                    for (const chunk of chunksFile) {
                        uint8.set(chunk, offset);
                        offset += chunk.byteLength;
                    }
                    const blobFile = new Blob([uint8]);

                    const cameraName = this._multipleDonwloadService.sanitizeFileName(currentFile?.nombre_camara || 'SIN_CAMARA');
                    const fileName = this._multipleDonwloadService.sanitizeFileName(currentFile.archivo || `${cameraName}-${Date.now()}.mp4`);
                    const cameraFolderHandle = await directoryHandle.getDirectoryHandle(cameraName, { create: true });
                    const fileHandle = await cameraFolderHandle.getFileHandle(fileName, { create: true });
                    const writable = await fileHandle.createWritable();
                    await writable.write(blobFile);
                    await writable.close();

                    downloadsSummary.success++;
                    totalSizeFilesDownloaded += contentLength || blobFile.size;

                    // marcar el archivo completado
                    this.currentFileProgress$.next(100);

                    const percentAfterFile = totalBytes > 0 ? Math.round((totalSizeFilesDownloaded / totalBytes) * 100) : 100;
                    this.progress$.next(Math.min(100, percentAfterFile));

                    if (index === downloadsResp.length - 1) {
                        downloadsSummary.totalSize = this._sharedService.formatFileSize(totalSizeFilesDownloaded);
                    }
                } catch {
                    downloadsSummary.errors++;
                    this.donwloadsFailed.push(currentFile);
                    const assumed = fileSizes[index] || 0;
                    totalSizeFilesDownloaded += assumed;
                    const percentFailedAdvance = totalBytes > 0 ? Math.round((totalSizeFilesDownloaded / totalBytes) * 100) : 0;
                    this.progress$.next(Math.min(100, percentFailedAdvance));
                    continue;
                }
            }

            // resultado y diálogo final
            if (downloadsSummary.errors === downloadsSummary.totalFiles) {
                downloadsSummary.message = this.translations?.no_se_pudo_descargar_ningun_archivo ?? 'No se pudo descargar ningún archivo';
            } else {
                downloadsSummary.message = `${this.translations?.los_archivos_descargados_se_han_guardado_en_la_carpeta ?? 'Los archivos descargados se han guardado en la carpeta'} "${directoryHandle.name}"`;
            }

            const dialogType = downloadsSummary.success === 0 && downloadsSummary.errors > 0 ? 'error' : 'success';
            const { archivos = 'Archivos', totales = 'totales', total = 'Total', n_descargas = 'descargas', errores = 'errores', Tabla_Tamano = 'Tamaño' } =
                (window as any).translations ?? {};

            try {
                const resUser = await this.messageService.dialogMessage(dialogType, downloadsSummary.message, {
                    confirmButtonText: (window as any).translations?.aceptar ?? this.translateService.instant('aceptar'),
                    showDenyButton: downloadsSummary.errors > 0,
                    denyButtonText: `${(window as any).translations?.mostrar ?? 'Mostrar'} ${(window as any).translations?.n_descargas ?? 'descargas'} ${(window as any).translations?.fallidas ?? 'fallidas'}`,
                    denyButtonColor: '#d33',
                    html: `
            <div>
              <p><b>${archivos} ${totales}:</b> ${downloadsSummary.totalFiles}</p>
              <p><b>${total} ${n_descargas}:</b> ${downloadsSummary.success}</p>
              <p><b>${total} ${errores}:</b> ${downloadsSummary.errors}</p>
              <p><b>${Tabla_Tamano} ${total} ${n_descargas}:</b> ${downloadsSummary.totalSize}</p>
            </div>
          `
                });
                if (downloadsSummary.errors > 0 && resUser.isDenied) {
                    if (typeof (window as any).viewDownloadsFailed === 'function') (window as any).viewDownloadsFailed();
                } else {
                    this.donwloadsFailed = [];
                }
            } catch (dialogErr) {
                console.error('Error mostrando diálogo final', dialogErr);
            }

            this.resetState();
            return downloadsSummary;
        } catch (err) {
            this.resetState();
            if (err?.toString?.().includes('The user aborted a request')) {
                throw this.translations?.elegir_carpeta_para_guardar_archivos
                    ?? this.translateService.instant('elegir_carpeta_para_guardar_archivos');
            }
            if (err?.toString?.().includes('Permiso de escritura denegado')) {
                throw this.translations?.debes_conceder_permiso_para_guardar_los_archivos
                    ?? this.translateService.instant('debes_conceder_permiso_para_guardar_los_archivos');
            }
            throw err ?? null;
        }
    }

    /** 
     * descarga un solo archivo y lanza la descarga en el navegador usando el UI flotante
     * @param elem - elemento fila
     */
    public async downloadSingleFile(elem: any): Promise<void> {
        if (!elem) return;

        // inicializar estado UI
        this.active$.next(true);
        this.currentIndex$.next(1);
        this.currentFileName$.next(elem?.nombre_camara ?? elem?.archivo ?? 'archivo');
        this.currentFileProgress$.next(0);
        this.progress$.next(0);

        try {
            // intentar resolver URL usando distintos endpoints (adaptable)
            let urlFile: string | null = null;
            try {
            if (typeof (this.databaseService as any).getTimelapseVideo === 'function') {
                const res: any = await lastValueFrom((this.databaseService as any).getTimelapseVideo(elem.archivo));
                urlFile = res?.urlDownload ?? res?.Descargas?.url ?? res?.url ?? null;
            } 
            if (!urlFile && typeof (this.databaseService as any).traerUrlVideo === 'function') {
                const res: any = await lastValueFrom((this.databaseService as any).traerUrlVideo(elem));
                urlFile = res?.Descargas?.url ?? res?.url ?? null;
            }
            } catch {
                this.messageService.SwalMessage('error', this.translations.haOcurridoUnError);
            }
            urlFile = urlFile || elem?.url || null;

            if (!urlFile) throw new Error('URL no disponible');

            // avance inicial
            this.currentFileProgress$.next(10);
            this.progress$.next(10);

            // fetch con posibilidad de streaming para actualizar progreso
            this.controller = new AbortController();
            const resp = await fetch(urlFile, { signal: this.controller.signal });
            if (!resp.ok) throw new Error('Error al obtener el recurso');

            const contentLengthHeader = resp.headers.get('Content-Length');
            const total = contentLengthHeader ? +contentLengthHeader : 0;
            const reader = resp.body?.getReader();

            if (reader) {
            const chunks: Uint8Array[] = [];
            let received = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                if (value) {
                const chunk = value instanceof Uint8Array ? value : new Uint8Array(value as ArrayBuffer);
                chunks.push(chunk);
                received += chunk.byteLength;

                const pct = total > 0 ? Math.floor((received / total) * 100) : 0;
                this.currentFileProgress$.next(pct);
                this.progress$.next(pct);
                }
                if (this.isCancelDownloads) break;
            }

            if (this.isCancelDownloads) {
                try { this.controller.abort(); } catch { /* noop */ }
                throw new Error('Descarga cancelada');
            }

            // concatenar y crear blob
            const totalLen = chunks.reduce((s, c) => s + c.byteLength, 0);
            const uint8 = new Uint8Array(totalLen);
            let offset = 0;
            for (const c of chunks) { uint8.set(c, offset); offset += c.byteLength; }
            const blob = new Blob([uint8]);

            // forzar 100% antes de la descarga
            this.currentFileProgress$.next(100);
            this.progress$.next(100);

            // trigger descarga en navegador
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = elem?.archivo?.split('/').pop() || `${(elem?.nombre_camara ?? 'video')}.mp4`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);

            } else {
            // fallback: no hay stream -> blob() directo
            const blob = await resp.blob();
            this.currentFileProgress$.next(100);
            this.progress$.next(100);

            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = elem?.archivo?.split('/').pop() || `${(elem?.nombre_camara ?? 'video')}.mp4`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
            }

            // opcional: marcar en backend como DISPONIBLE (no lo hago aquí, lo harás desde el componente)
        } finally {
            // dejar el progreso 100% visible un momento y limpiar estado
            setTimeout(() => this.resetState(), 300);
        }
    }

    /**
     * Cancela la tanda de descargas en curso
     */
    cancelBatch() {
        this.isCancelDownloads = true;
        try { this.controller?.abort(); } catch (e) { console.error('Error abortando descargas', e); }
        this.resetState();
    }

    /**
     * Resetea el estado del servicio después de completar o cancelar una tanda de descargas
     */
    private resetState() {
        this.controller = null;
        this.isCancelDownloads = false;
        this.progress$.next(0);
        this.active$.next(false);
        this.batchInfo$.next(null);

        this.currentIndex$.next(null);
        this.currentFileProgress$.next(0);
        this.currentFileName$.next(null);
    }

    private get translations(): any { return (window as any).translations ?? {}; }
}
